import { RunConfigCase, RunStatus } from '@aero-platform/shared/generated/AeroStructures'
import { RunStatusCircle } from './StatusCircle'
import { RunStatusDatatableText } from './DataTableText'

// Minimal shape needed to convert the drafting offset (meters) into car length/width multiples.
export type VehicleDimensions = {
  length_m?: number
  width_m?: number
}

export type DraftingOffsetInfo = {
  xOffset: string
  yOffset: string
  leadOrTrail: 'Lead' | 'Trail'
}

// Computes the Drafting (following car) offset for a case, converted to car length/width
// multiples using the vehicle's real dimensions, plus a Lead/Trail indicator. Returns
// undefined for non-drafting cases or when the vehicle's dimensions aren't available to
// compute the conversion.
export const getDraftingOffsetInfo = (
  rowData: RunConfigCase,
  vehicle?: VehicleDimensions
): DraftingOffsetInfo | undefined => {
  const localPosition = rowData.selectedFollowingCar?.localPosition
  if (!localPosition || !vehicle?.length_m || !vehicle?.width_m) {
    return undefined
  }
  return {
    xOffset: (Number(localPosition.distance_x_m) / vehicle.length_m).toFixed(2),
    yOffset: (Number(localPosition.distance_y_m) / vehicle.width_m).toFixed(2),
    leadOrTrail: rowData.followingCarCase ? 'Trail' : 'Lead'
  }
}

// For Drafting (following car) cases, appends the trailing car's offset (converted to car
// lengths/widths using the vehicle's real dimensions) plus a Lead/Trail indicator, so cases
// sharing the same attitude state can still be told apart, e.g.
// "State_1 Drafting X=1.850 L Y=0.620 W Trail". Falls back to the plain case name if the
// vehicle's dimensions aren't available to compute the conversion.
export const getDraftingCaseDisplayName = (
  rowData: RunConfigCase,
  vehicle?: VehicleDimensions
): string => {
  const offsetInfo = getDraftingOffsetInfo(rowData, vehicle)
  if (!offsetInfo) {
    return rowData.name
  }
  const { xOffset, yOffset, leadOrTrail } = offsetInfo
  return `${rowData.name} Drafting X=${xOffset} L Y=${yOffset} W ${leadOrTrail}`
}

export const runCaseNameBodyTemplates = (
  rowData: RunConfigCase,
  status: RunStatus | undefined,
  vehicle?: VehicleDimensions
) => {
  // note: need Workflow's (overallStatus?: RunStatus;), so can add supplumental value to body template
  // dependant on workflowTasks' WorkflowTaskStatus.runStatus
  return (
    <div className="flex items-center">
      <div className="flex justify-center items-center">
        {' '}
        {/* border-solid border-2 border-black*/}
        <RunStatusCircle status={status} />
      </div>
      <div className="flex flex-col ">
        <RunStatusDatatableText status={status}>
          {getDraftingCaseDisplayName(rowData, vehicle)}
        </RunStatusDatatableText>
      </div>
    </div>
  )
}
